import React, { useCallback, useState } from 'react';
import { formatMonthDayTime } from 'config/time';
import user_logo from '../../../assets/icons/user.svg';
import VideoModal from 'reusable/videopreview';
import { useTranslation } from 'react-i18next';
import { MEASUREMENT_CELL, measurementState } from 'config/measurement';
import EvidenceCell, { EvidenceRow, useEvidenceCheck } from 'reusable/EvidenceCell';
import DeviceTimeZone from 'reusable/DeviceTimeZone';
import TableScroll, { PAGE_TITLE_ID, tabId } from 'reusable/TableScroll';

interface Submission extends EvidenceRow {
  user?: {
    name: string;
    phone: string;
    profile_picture?: string;
    /** EV-450。消去の依頼で、法令で残す測定記録のために氏名だけを残した運転者。 */
    is_erased?: boolean;
  };
  data: string;
  testing_time: string;
  device_time_zone?: string | null;
  device_utc_offset_minutes?: number | null;
}

interface ExceedAlcoholLimitsTableProps {
  submissions: Submission[];
  /** 一覧の条件（タブ・検索・並び・日付・ページ）。変わったら確認の応答を捨て、動画を閉じる（RV-166）。 */
  queryKey?: string;
}

const ExceedAlcoholLimitsTable: React.FC<ExceedAlcoholLimitsTableProps> = ({
  submissions,
  queryKey = '',
}) => {
  const { t } = useTranslation(['alcohol', 'common']);
  const [showVideo, setShowVideo] = useState(false);
  const [currentVideoLink, setCurrentVideoLink] = useState('');

  const handleVideoClick = useCallback((videoLink: string) => {
    setCurrentVideoLink(videoLink);
    setShowVideo(true);
  }, []);
  const closeVideo = useCallback(() => setShowVideo(false), []);
  // EV-193・RV-166。未確定の行の「確認して再生」。一覧の行か条件が変わったら、前の確認の応答は捨て、
  // 開いている動画を閉じる。
  const evidenceCheck = useEvidenceCheck(handleVideoClick, submissions, queryKey, closeVideo);

  const getColorClass = (dataValue: string) => {
    // BE-27・TH-AC3。件数・絞り込み（data__gte）と同じ「閾値以上」で赤にする。
    const state = measurementState(dataValue);
    if (state === 'exceeded') {
      return `${MEASUREMENT_CELL.exceeded} px-2 rounded-md flex items-center justify-center w-24`;
    } else if (state === 'clear') {
      return `${MEASUREMENT_CELL.clear} px-2 rounded-md flex items-center justify-center w-24`;
    } else {
      return `${MEASUREMENT_CELL.detected} px-2 rounded-md flex items-center justify-center w-24`;
    }
  };

  return (
    <>
      {showVideo && (
        <VideoModal
          videoLink={currentVideoLink}
          onClose={() => setShowVideo(false)}
        />
      )}
      <main className="flex flex-col my-4">
        {/* EV-301。狭い幅では表に最小の幅を持たせ、表の外枠の中だけを横に送る。 */}
        <TableScroll labelledBy={`${PAGE_TITLE_ID} ${tabId('exceedalcohollimits')}`}>
        {/* 2026-11-04 PM。時刻と日付の2列を「日時」の1列にまとめた（6列→5列）。 */}
        <div className="grid grid-cols-5 gap-4 p-2 bg-primary/50 rounded-md">
          <h6 className="w-40">{t('table.driver-name')}</h6>
          <h6 className="w-24">{t('table.test-result')}</h6>
          {/* EV-297。証跡の列の見出しも列の幅に合わせ、160px を上限にする（狭い幅で右隣の「時刻」に重ならない）。
              欄と同じく、語より狭くはしない（min-w-min。英語の Submissions が見出しの枠からはみ出さない）。 */}
          <h6 className="w-full max-w-40 min-w-min">{t('table.video-submission')}</h6>
          <h6 className="w-40">{t('table.datetime')}</h6>
          {/* EV-301。最後の列の見出しの箱も列の幅に合わせ 160px を上限にする（w-40 の箱が表の右へはみ出し、1024px で画面の
              中身が 10px 横に送れていた）。文字は今までどおり1行で出す（whitespace-nowrap。1024px の英語の
              Device Time Zone は箱より長く、折ると見た目が変わる）。
              EV-321（docs/testing/parallel-wave25_2026-10-14/table-width/README.md）。外枠を送らない幅（TableScroll の境
              46.75rem 以上）でだけ折り返せる。箱は列の幅に見出しの帯の右の余白（p-2＝0.5rem）を足した幅にし、文字が外枠を越える
              ときだけ折る（英語の 1024〜約1120px で「Device Time / Zone」の2行。それより広い幅と日本語は今までどおり1行）。
              外枠を送る幅（狭い幅）は今までどおり1行で、外枠の中で送る。 */}
          <h6 className="w-[calc(100%+0.5rem)] max-w-40 whitespace-nowrap [@container(min-width:46.75rem)]:whitespace-normal">{t('time-zone.device', { ns: 'common' })}</h6>
        </div>
        {submissions.length === 0 ? null : (
          submissions.map((submission, index) => (
            <div key={index} className="grid grid-cols-5 gap-4 p-2 border-b">
              <div className="flex space-x-2 items-center">
                <img
                  src={submission.user?.profile_picture || user_logo}
                  alt=""
                  className="h-10 w-10 object-cover rounded-full"
                />
                <div>
                  <div className="w-32">
                    {submission.user?.name || '-'}
                  </div>
                  <div className="text-caption w-32">
                    {submission.user?.is_erased ? t('table.erased') : (submission.user?.phone || '-')}
                  </div>
                </div>
              </div>
              <div className={getColorClass(submission.data)}>
                {submission.data} mg/L
              </div>
              {/* EV-297。欄は列の幅に合わせ、160px（w-40）を上限にする。幅を固定すると、列が 160px より狭い幅で列から
                  はみ出し、右隣の時刻に重なる。中身が収まらないときは、EvidenceCell が中身の最小の幅まで広げる（EV-292）。 */}
              <EvidenceCell
                submission={submission}
                evidenceCheck={evidenceCheck}
                rowLabel={`${submission.user?.name ?? ''} ${formatMonthDayTime(submission.testing_time)} ID ${submission.id ?? ''}`}
                className="w-full max-w-40 border flex items-center rounded-md justify-center"
                iconClassName="mr-2"
              />
              {/* EV-116。業務の時間帯（会社の時間帯）で出す。2026-11-04 PM。月日 時分秒。 */}
              <div className="w-40 whitespace-nowrap">
                {formatMonthDayTime(submission.testing_time)}
              </div>
              {/* EV-116 段階3。測定の時刻の時点の端末の時間帯。 */}
              {/* EV-301。最後の列の箱は列の幅に合わせ 160px を上限にする（見出しと同じ）。文字は左寄せで短いので見た目は
                  変わらない。 */}
              <DeviceTimeZone
                className="w-full max-w-40"
                zone={submission.device_time_zone}
                offset={submission.device_utc_offset_minutes}
                at={submission.testing_time}
              />
            </div>
          ))
        )}
        </TableScroll>
        {/* PR #244 の Codex の指摘。データが無いときの文は外枠（最小の幅の中身）の外に置き、見える幅の中央に出す。 */}
        {submissions.length === 0 && <div className="text-center py-2 mt-10">{t('table.no-data')}</div>}
      </main>
    </>
  );
};

export default ExceedAlcoholLimitsTable;
